import { useState, useEffect, useMemo } from "react";
import API, { formatError } from "@/api";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import { Sparkles, Loader2, RefreshCw, Trophy, TrendingDown, Clock, CheckCircle2, AlertTriangle, ListChecks } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const COLORS = ["#002FA7", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

export default function ReportsPage() {
    const { user } = useAuth();
    const isAdmin = ["super_admin", "admin"].includes(user?.role);
    const canUseAI = isAdmin || !!user?.leads_any_team;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [topUsers, setTopUsers] = useState(null);
    const [topUsersLoading, setTopUsersLoading] = useState(true);
    const [workspaces, setWorkspaces] = useState([]);
    const [teams, setTeams] = useState([]);
    const [boards, setBoards] = useState([]);

    const [selectedWs, setSelectedWs] = useState("");
    const [selectedTeam, setSelectedTeam] = useState("");
    const [selectedBoard, setSelectedBoard] = useState("");

    // AI summary state
    const [aiSummary, setAiSummary] = useState("");
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        API.get("/workspaces").then(r => setWorkspaces(r.data)).catch(() => {});
    }, []);

    // When workspace changes, fetch teams
    useEffect(() => {
        setSelectedTeam("");
        setSelectedBoard("");
        if (!selectedWs) { setTeams([]); setBoards([]); return; }
        API.get(`/teams?workspace_id=${selectedWs}`).then(r => setTeams(r.data)).catch(() => setTeams([]));
    }, [selectedWs]);

    // When team changes, fetch boards
    useEffect(() => {
        setSelectedBoard("");
        if (!selectedTeam) { setBoards([]); return; }
        API.get(`/boards?team_id=${selectedTeam}`).then(r => setBoards(r.data)).catch(() => setBoards([]));
    }, [selectedTeam]);

    // Fetch report
    useEffect(() => {
        setLoading(true);
        setTopUsersLoading(true);
        setAiSummary("");
        const params = new URLSearchParams();
        if (selectedWs) params.append("workspace_id", selectedWs);
        if (selectedTeam) params.append("team_id", selectedTeam);
        if (selectedBoard) params.append("board_id", selectedBoard);
        const qs = params.toString() ? `?${params.toString()}` : "";
        API.get(`/reports/overview${qs}`)
            .then(r => setData(r.data))
            .catch(() => toast.error("Eroare la încărcarea raportului"))
            .finally(() => setLoading(false));
        API.get(`/reports/top-users${qs}`)
            .then(r => setTopUsers(r.data))
            .catch(() => setTopUsers({ users: [], top_performer_id: null, under_performer_id: null }))
            .finally(() => setTopUsersLoading(false));
    }, [selectedWs, selectedTeam, selectedBoard]);

    const [aiError, setAiError] = useState("");
    const generateAISummary = async () => {
        setAiLoading(true);
        setAiSummary("");
        setAiError("");
        try {
            const payload = { scope: "overview" };
            if (selectedWs) payload.workspace_id = selectedWs;
            if (selectedTeam) payload.team_id = selectedTeam;
            if (selectedBoard) payload.board_id = selectedBoard;
            const r = await API.post("/reports/ai-summary", payload);
            setAiSummary(r.data.summary);
        } catch (err) {
            const msg = formatError(err);
            setAiError(msg);
            toast.error(msg);
        } finally {
            setAiLoading(false);
        }
    };

    const scopeLabel = useMemo(() => {
        if (selectedBoard) return boards.find(b => b.id === selectedBoard)?.name || "Board selectat";
        if (selectedTeam) return teams.find(t => t.id === selectedTeam)?.name || "Echipa selectată";
        if (selectedWs) return workspaces.find(w => w.id === selectedWs)?.name || "Workspace selectat";
        return "Întreaga organizație";
    }, [selectedWs, selectedTeam, selectedBoard, workspaces, teams, boards]);

    return (
        <div className="p-6 max-w-6xl mx-auto" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <div className="mb-6">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Rapoarte</h1>
                        <p className="text-xs text-[#9CA3AF] mt-0.5">Scope: <span className="font-semibold text-[#4B5563] dark:text-[#C4C9D4]">{scopeLabel}</span></p>
                    </div>
                    {canUseAI && (
                        <button onClick={generateAISummary} disabled={aiLoading || loading}
                            data-testid="ai-summary-btn"
                            className="flex items-center gap-2 text-xs font-semibold bg-gradient-to-r from-[#002FA7] to-[#4F46E5] text-white px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-60 transition-opacity">
                            {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                            {aiLoading ? "Se generează..." : (aiSummary ? "Regenerează sinteza AI" : "Generează sinteză AI (Claude 4.5)")}
                        </button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-2" data-testid="report-filters">
                    <select data-testid="report-workspace-filter" value={selectedWs} onChange={e => setSelectedWs(e.target.value)}
                        className="px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]">
                        <option value="">Toate workspace-urile</option>
                        {workspaces.map(ws => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
                    </select>
                    <select data-testid="report-team-filter" value={selectedTeam} onChange={e => setSelectedTeam(e.target.value)}
                        disabled={!selectedWs || teams.length === 0}
                        className="px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:opacity-60">
                        <option value="">Toate echipele</option>
                        {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                    <select data-testid="report-board-filter" value={selectedBoard} onChange={e => setSelectedBoard(e.target.value)}
                        disabled={!selectedTeam || boards.length === 0}
                        className="px-3 py-2 border border-[#E4E4E7] dark:border-[#252B33] dark:bg-[#151A21] dark:text-[#F3F4F6] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] disabled:opacity-60">
                        <option value="">Toate board-urile</option>
                        {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                    </select>
                    {(selectedWs || selectedTeam || selectedBoard) && (
                        <button onClick={() => { setSelectedWs(""); setSelectedTeam(""); setSelectedBoard(""); }}
                            data-testid="report-clear-filters"
                            className="text-xs text-[#9CA3AF] hover:text-[#002FA7] px-2 py-1 flex items-center gap-1">
                            <RefreshCw size={11} /> Resetează
                        </button>
                    )}
                </div>
            </div>

            {/* AI Summary panel */}
            {canUseAI && (aiSummary || aiLoading || aiError) && (
                <div className="mb-6 bg-gradient-to-br from-[#EEF2FF] to-[#F5F7FF] dark:from-[#1A2338] dark:to-[#151A21] border border-[#C7D2FE] dark:border-[#2A3A5E] rounded-md p-5" data-testid="ai-summary-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-[#002FA7] dark:text-[#6A91FF]" />
                        <h3 className="font-bold text-sm text-[#002FA7] dark:text-[#6A91FF]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            Sinteză AI (Claude Sonnet 4.5)
                        </h3>
                    </div>
                    {aiLoading ? (
                        <div className="flex items-center gap-2 text-sm text-[#4B5563] dark:text-[#C4C9D4]">
                            <Loader2 size={14} className="animate-spin" /> Claude analizează datele...
                        </div>
                    ) : aiError && !aiSummary ? (
                        <div className="text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300 rounded p-3" data-testid="ai-summary-error">
                            <div className="font-semibold mb-1">Sinteza nu a putut fi generată</div>
                            <div className="text-xs opacity-90">{aiError}</div>
                            <button onClick={generateAISummary} className="mt-2 text-xs underline hover:no-underline">Reîncearcă</button>
                        </div>
                    ) : (
                        <AISummaryFormatted text={aiSummary} />
                    )}
                </div>
            )}

            {loading ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md animate-pulse" />)}
                </div>
            ) : data ? (
                <>
                    {/* Summary stats */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="report-stats">
                        {[
                            { label: "Total taskuri", value: data.total_tasks, color: "text-[#002FA7] dark:text-[#6A91FF]" },
                            { label: "Finalizate", value: data.completed_tasks, color: "text-green-600 dark:text-green-400" },
                            { label: "Întârziate", value: data.overdue_tasks, color: "text-red-600 dark:text-red-400" },
                            { label: "În lucru", value: data.total_tasks - data.completed_tasks, color: "text-orange-500 dark:text-orange-400" },
                        ].map(stat => (
                            <div key={stat.label} className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5" data-testid={`report-stat-${stat.label}`}>
                                <p className={`text-3xl font-bold ${stat.color}`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{stat.value}</p>
                                <p className="text-xs text-[#9CA3AF] mt-1 font-medium uppercase tracking-wide">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Tasks by Status */}
                        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5" data-testid="chart-by-status">
                            <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Taskuri pe status</h3>
                            {data.by_status?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={data.by_status} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
                                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                                        <YAxis tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                                        <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: "6px", fontSize: 12 }} />
                                        <Bar dataKey="value" fill="#002FA7" radius={[4, 4, 0, 0]} name="Taskuri" />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : <div className="h-52 flex items-center justify-center text-sm text-[#9CA3AF]">Nicio dată</div>}
                        </div>

                        {/* Tasks by Priority */}
                        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5" data-testid="chart-by-priority">
                            <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Taskuri pe prioritate</h3>
                            {data.by_priority?.some(d => d.value > 0) ? (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={data.by_priority.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                                            {data.by_priority.filter(d => d.value > 0).map((_, index) => (
                                                <Cell key={index} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: "6px", fontSize: 12 }} />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : <div className="h-52 flex items-center justify-center text-sm text-[#9CA3AF]">Nicio dată</div>}
                        </div>
                    </div>

                    {/* Workload by assignee */}
                    {data.by_assignee?.length > 0 && (
                        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5 mb-6" data-testid="chart-by-assignee">
                            <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Workload pe utilizator</h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={data.by_assignee} layout="vertical" margin={{ top: 0, right: 20, left: 60, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F3F5" />
                                    <XAxis type="number" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#9CA3AF" }} width={60} />
                                    <Tooltip contentStyle={{ border: "1px solid #E4E4E7", borderRadius: "6px", fontSize: 12 }} />
                                    <Bar dataKey="value" fill="#10B981" radius={[0, 4, 4, 0]} name="Taskuri" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Top Users / Performance ranking */}
                    <TopUsersSection data={topUsers} loading={topUsersLoading} />
                </>
            ) : null}
        </div>
    );
}

function TopUsersSection({ data, loading }) {
    if (loading) {
        return <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5 h-48 animate-pulse" />;
    }
    if (!data || !data.users || data.users.length === 0) {
        return (
            <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5" data-testid="top-users-empty">
                <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Top utilizatori</h3>
                <p className="text-sm text-[#9CA3AF]">Niciun task atribuit în acest scope.</p>
            </div>
        );
    }
    const topId = data.top_performer_id;
    const underId = data.under_performer_id;

    const scoreColor = (s) => {
        if (s >= 75) return "text-green-600 dark:text-green-400";
        if (s >= 50) return "text-blue-600 dark:text-blue-400";
        if (s >= 25) return "text-orange-500 dark:text-orange-400";
        return "text-red-600 dark:text-red-400";
    };
    const scoreBg = (s) => {
        if (s >= 75) return "bg-green-50 dark:bg-green-900/20";
        if (s >= 50) return "bg-blue-50 dark:bg-blue-900/20";
        if (s >= 25) return "bg-orange-50 dark:bg-orange-900/20";
        return "bg-red-50 dark:bg-red-900/20";
    };

    return (
        <div className="bg-white dark:bg-[#151A21] border border-[#E4E4E7] dark:border-[#252B33] rounded-md p-5" data-testid="top-users-section">
            <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-sm text-[#111827] dark:text-[#F3F4F6]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Top utilizatori</h3>
                <span className="text-[11px] text-[#9CA3AF]">{data.users.length} utilizator{data.users.length === 1 ? "" : "i"}</span>
            </div>
            <p className="text-xs text-[#9CA3AF] mb-4">Clasament după scor de productivitate (50% volum • 30% rata la timp • 20% lipsă întârzieri).</p>

            <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="top-users-table">
                    <thead>
                        <tr className="text-[10px] uppercase font-bold text-[#9CA3AF] tracking-wider border-b border-[#F1F3F5] dark:border-[#252B33]">
                            <th className="text-left py-2 pl-2">#</th>
                            <th className="text-left py-2">Utilizator</th>
                            <th className="text-right py-2 pr-2" title="Task-uri finalizate"><CheckCircle2 size={11} className="inline -mt-0.5" /> Finalizate</th>
                            <th className="text-right py-2 pr-2" title="Task-uri active (în lucru)">Active</th>
                            <th className="text-right py-2 pr-2 text-red-500" title="Active cu termen depășit"><AlertTriangle size={11} className="inline -mt-0.5" /> Întârziate</th>
                            <th className="text-right py-2 pr-2" title="% finalizări la timp">La timp</th>
                            <th className="text-right py-2 pr-2" title="Zile medii de la creare la finalizare"><Clock size={11} className="inline -mt-0.5" /> Zile medii</th>
                            <th className="text-right py-2 pr-2" title="Puncte checklist bifate"><ListChecks size={11} className="inline -mt-0.5" /> Checklist</th>
                            <th className="text-right py-2 pr-3" title="Scor 0-100">Scor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.users.map((u, idx) => {
                            const isTop = u.user_id === topId;
                            const isUnder = u.user_id === underId;
                            return (
                                <tr key={u.user_id}
                                    data-testid={`top-user-row-${u.user_id}`}
                                    className={`border-b border-[#F1F3F5] dark:border-[#252B33] last:border-0 hover:bg-[#F8F9FA] dark:hover:bg-[#1C222A] transition-colors
                                        ${isTop ? "bg-amber-50/40 dark:bg-amber-900/10" : ""}
                                        ${isUnder ? "bg-red-50/30 dark:bg-red-900/10" : ""}`}>
                                    <td className="py-2.5 pl-2 text-[#9CA3AF] font-semibold text-xs">{idx + 1}</td>
                                    <td className="py-2.5">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.avatar || "#002FA7" }}>
                                                <span className="text-white text-xs font-bold">{(u.name || "?").charAt(0).toUpperCase()}</span>
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-1.5 truncate">
                                                    <span className="font-semibold text-[#111827] dark:text-[#F3F4F6] truncate">{u.name}</span>
                                                    {isTop && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded uppercase tracking-wider" data-testid={`top-performer-badge-${u.user_id}`}>
                                                            <Trophy size={9} /> Top performer
                                                        </span>
                                                    )}
                                                    {isUnder && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-1.5 py-0.5 rounded uppercase tracking-wider" data-testid={`under-performer-badge-${u.user_id}`}>
                                                            <TrendingDown size={9} /> Atenție
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[10px] text-[#9CA3AF] truncate">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="py-2.5 pr-2 text-right tabular-nums font-semibold text-green-600 dark:text-green-400">{u.completed_count}</td>
                                    <td className="py-2.5 pr-2 text-right tabular-nums text-[#4B5563] dark:text-[#C4C9D4]">{u.active_count}</td>
                                    <td className={`py-2.5 pr-2 text-right tabular-nums font-semibold ${u.overdue_count > 0 ? "text-red-600 dark:text-red-400" : "text-[#9CA3AF]"}`}>{u.overdue_count}</td>
                                    <td className="py-2.5 pr-2 text-right tabular-nums text-[#4B5563] dark:text-[#C4C9D4]">{u.completed_count > 0 ? `${u.on_time_rate}%` : "—"}</td>
                                    <td className="py-2.5 pr-2 text-right tabular-nums text-[#4B5563] dark:text-[#C4C9D4]">{u.avg_completion_days !== null ? `${u.avg_completion_days}z` : "—"}</td>
                                    <td className="py-2.5 pr-2 text-right tabular-nums text-[#4B5563] dark:text-[#C4C9D4]">{u.checklist_done_count}</td>
                                    <td className="py-2.5 pr-3 text-right">
                                        <span className={`inline-flex items-center justify-end min-w-[44px] px-2 py-0.5 rounded text-xs font-bold tabular-nums ${scoreBg(u.productivity_score)} ${scoreColor(u.productivity_score)}`}>
                                            {u.productivity_score}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function AISummaryFormatted({ text }) {
    if (!text) return null;
    // Parsare în secțiuni delimitate de **titlu** (markdown bold)
    const sections = [];
    let current = { title: null, body: [] };
    const lines = String(text).split("\n");
    for (const raw of lines) {
        const line = raw.trim();
        const titleMatch = line.match(/^\*\*(.+?)\*\*\s*:?\s*$/);
        if (titleMatch) {
            if (current.title || current.body.length) sections.push(current);
            current = { title: titleMatch[1].trim(), body: [] };
        } else if (line.length) {
            current.body.push(line);
        }
    }
    if (current.title || current.body.length) sections.push(current);

    // Fallback: dacă nu detectează secțiuni, afișează tot ca text simplu
    if (sections.length <= 1 && !sections[0]?.title) {
        return (
            <div className="text-sm text-[#111827] dark:text-[#F3F4F6] whitespace-pre-wrap leading-relaxed" data-testid="ai-summary-text">
                {text}
            </div>
        );
    }

    return (
        <div className="space-y-4 text-sm text-[#111827] dark:text-[#F3F4F6]" data-testid="ai-summary-text">
            {sections.map((s, idx) => (
                <div key={idx} className="bg-white/60 dark:bg-[#151A21]/60 rounded-md p-3 border border-white/80 dark:border-[#252B33]">
                    {s.title && (
                        <h4 className="font-bold text-[13px] mb-2 text-[#002FA7] dark:text-[#6A91FF] tracking-tight"
                            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                            {s.title}
                        </h4>
                    )}
                    <div className="space-y-1">
                        {s.body.map((b, i) => {
                            const bulletMatch = b.match(/^[-•*]\s+(.+)$/);
                            if (bulletMatch) {
                                return (
                                    <div key={i} className="flex gap-2 leading-relaxed">
                                        <span className="text-[#002FA7] dark:text-[#6A91FF] flex-shrink-0 mt-0.5">•</span>
                                        <span className="flex-1">{bulletMatch[1].replace(/\*\*(.+?)\*\*/g, "$1")}</span>
                                    </div>
                                );
                            }
                            return (
                                <p key={i} className="leading-relaxed">{b.replace(/\*\*(.+?)\*\*/g, "$1")}</p>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
